import { useCallback } from 'react';
import { api } from '../lib/api';
import { formatCount, formatDate } from '../lib/format';
import { go, href } from '../lib/route';
import { useLoad } from '../lib/useLoad';
import { AccountLink, Loading, Pager, Pill, Search } from '../components/bits';
import { PageHead } from '../components/Shell';

interface Props {
  readonly token: string;
  readonly query: URLSearchParams;
}

export const Frames = ({ token, query }: Props) => {
  const q = query.get('q') ?? '';
  const accountId = query.get('accountId') ?? '';
  const offset = Number(query.get('offset')) || 0;

  const loaded = useLoad(
    () => api.listDevices(token, { q, accountId, offset }),
    [token, q, accountId, offset],
  );

  const onSearch = useCallback((next: string) => go('/frames', { q: next, accountId }), [accountId]);

  return (
    <>
      <PageHead
        eyebrow="Frames"
        title="Every frame"
        lede="A frame is a photo library and its sharing rules — what the circles proposal calls a circle."
        actions={<Search value={q} placeholder="Frame name or owner email" onSearch={onSearch} />}
      />

      <Loading loaded={loaded}>
        {(page) => (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Frame</th>
                    <th scope="col">Owner</th>
                    <th scope="col">Created</th>
                    <th scope="col" className="num">
                      Photos
                    </th>
                    <th scope="col" className="num">
                      Pending
                    </th>
                    <th scope="col" className="num">
                      People
                    </th>
                    <th scope="col" className="num">
                      Screens
                    </th>
                    <th scope="col">Sharing</th>
                  </tr>
                </thead>
                <tbody>
                  {page.items.map((device) => (
                    <tr key={device.id}>
                      <td>
                        <a className="ref" href={href(`/frames/${device.id}`)}>
                          <span className="ref-name">{device.label}</span>
                        </a>
                      </td>
                      <td>
                        <AccountLink account={device.owner} />
                      </td>
                      <td>{formatDate(device.createdAt)}</td>
                      <td className="num">{formatCount(device.photoCount)}</td>
                      <td className="num">{formatCount(device.pendingCount)}</td>
                      <td className="num">{formatCount(device.memberCount)}</td>
                      <td className="num">{formatCount(device.screenCount)}</td>
                      <td>
                        {device.publicUpload && <Pill tone="wait">public link</Pill>}
                        {device.autoAcceptEveryone && <Pill tone="wait">auto-accept</Pill>}
                        {device.owner === null && <Pill tone="bad">unowned</Pill>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager page={page} noun="frames" onOffset={(next) => go('/frames', { q, accountId, offset: next })} />
          </>
        )}
      </Loading>
    </>
  );
};
